import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { API } from "@/lib/api";
import { CarIcon, SignInIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [appVersion, setAppVersion] = useState("");

  useEffect(() => {
    // Read the live plugin version so the login page never drifts from the
    // deployed WP plugin build number (single source of truth: the PHP header).
    fetch(`${API}/wp-sync/plugin-version`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d?.version && setAppVersion(d.version))
      .catch(() => {});
  }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Καλώς ήρθατε");
      nav("/");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Σφάλμα";
      toast.error(typeof msg === "string" ? msg : "Λάθος email ή κωδικός");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      {/* Left visual */}
      <div className="hidden md:block flex-1 bg-[#0A0A0A] relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?fm=jpg&q=80&w=2400&auto=format&fit=crop")' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/20" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div className="flex items-center gap-1">
            <span className="heading text-2xl font-extrabold tracking-tight leading-none">BESI</span>
          </div>
          <div>
            <h1 className="heading text-5xl font-extrabold tracking-tight leading-tight">
              Πλήρης έλεγχος<br />του στόλου σας.
            </h1>
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono" data-testid="login-app-version">
            v{appVersion || "…"}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">Σύνδεση</div>
            <h2 className="heading text-3xl font-extrabold tracking-tight mt-1">Καλώς ήρθατε</h2>
          </div>

          <div className="space-y-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#4B5563]">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                autoComplete="email"
                required
                className="px-3 py-2.5 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#4B5563]">Κωδικός</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                autoComplete="current-password"
                required
                className="px-3 py-2.5 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <SignInIcon size={18} />
            {loading ? "Σύνδεση..." : "Σύνδεση"}
          </button>

          <div className="text-xs text-[#4B5563] border-t border-black/10 pt-4">
            Πρώτη φορά; Χρησιμοποιήστε τα στοιχεία του διαχειριστή που σας έχουν δοθεί.
          </div>
        </form>
      </div>
    </div>
  );
}
